import { DayObject, DayRange, FixedDayRange } from "@/types/date"
import { PlaceRef } from "@/types/entity"
import { Room, TravelerGroup, Trip } from "@/types/trip"

import { defaultChildAges } from "@/lib/child-ages-memory"
import { assert } from "@/lib/utils/assertions"
import {
  addDays,
  maxDayObject,
  minDayObject,
  nightsBetweenDayObjects,
  rangeContainsDay,
} from "./date-utils"
import { dayRangeForDraftDates } from "./time-frame-utils"

export function tripDayCount(trip: Trip): number | undefined {
  return trip.draft_duration
}

export function draftTripDates(trip: Trip): FixedDayRange {
  return dayRangeForDraftDates(trip.draft_start_date, trip.draft_duration)
}

export function getTripDates(trip: Trip): DayRange {
  return dayRangeForDraftDates(trip.draft_start_date, trip.draft_duration)
}

export function getTripDuration(trip: Trip): number | undefined {
  return trip.draft_duration
}

export function getTripStopsAreaName(trip: Trip): string | undefined {
  return trip.stops_area_name ?? trip.stops_area?.name
}

export function getTripMajorStopsRefs(trip: Trip): PlaceRef[] {
  return (
    trip.major_stops_details
      ?.map((stop) => stop.location.entity_ref)
      .filter((ref): ref is PlaceRef => !!ref) ?? []
  )
}

export const DEFAULT_TRAVELERS: TravelerGroup = {
  adult_count: 1,
  child_count: 0,
  infant_count: 0,
  pet_count: 0,
}

export function defaultTravelers(): TravelerGroup {
  return DEFAULT_TRAVELERS
}

export function getTripTravelersOrDefault(trip: {
  travelers?: TravelerGroup | null
}): TravelerGroup {
  return trip.travelers ?? DEFAULT_TRAVELERS
}

export function defaultRooms(): Room[] {
  return [
    {
      adult_count: 2,
      child_ages: [],
      pet_count: 0,
    },
  ]
}

export function dayNumberForDay(
  date: DayObject,
  tripDates: FixedDayRange
): number {
  assert(rangeContainsDay(tripDates, date), "day is not in range")
  return 1 + nightsBetweenDayObjects(tripDates.from, date)
}

export function totalPeople(travelers: TravelerGroup): number {
  // omit pets
  return (
    (travelers.adult_count || 0) +
    (travelers.child_count || 0) +
    (travelers.infant_count || 0)
  )
}

/**
 * Resizes a party to `size` people by moving the difference onto the adult
 * count, so an existing child/infant/pet breakdown survives the edit. Children
 * and then infants are only dropped when the party is too small to hold them
 * alongside one adult.
 */
export function resizeTravelerParty(
  travelers: TravelerGroup,
  size: number
): TravelerGroup {
  const adultCount = size - travelers.child_count - travelers.infant_count
  if (adultCount >= 1) {
    return { ...travelers, adult_count: adultCount }
  }

  const seatsForMinors = Math.max(size - 1, 0)
  const childCount = Math.min(travelers.child_count, seatsForMinors)
  return {
    ...travelers,
    adult_count: 1,
    child_count: childCount,
    infant_count: Math.min(travelers.infant_count, seatsForMinors - childCount),
  }
}

export function roomToTravelerGroup(room: Room): TravelerGroup {
  return {
    adult_count: room.adult_count,
    child_count: room.child_ages.length,
    infant_count: 0,
    pet_count: room.pet_count,
  }
}

export function travelerGroupToRoom(travelers: TravelerGroup): Room {
  const child_ages = defaultChildAges(travelers.child_count)
  return {
    adult_count: travelers.adult_count,
    child_ages,
    pet_count: travelers.pet_count,
  }
}

/** True when room adult/child/pet counts match the traveler group. */
export function roomMatchesTravelers(
  room: Room,
  travelers: TravelerGroup
): boolean {
  return (
    room.adult_count === travelers.adult_count &&
    room.child_ages.length === travelers.child_count &&
    room.pet_count === travelers.pet_count
  )
}

/**
 * Prefer a stored room when its counts match travelers (keeps child ages);
 * otherwise derive a room from travelers. Avoids the guest editor showing a
 * stale room while the place-detail summary reads travelers.
 */
export function resolveRoomForTravelers(
  travelers: TravelerGroup | undefined,
  storedRoom: Room | undefined
): Room | undefined {
  if (!travelers) return storedRoom
  if (storedRoom && roomMatchesTravelers(storedRoom, travelers)) {
    return storedRoom
  }
  return travelerGroupToRoom(travelers)
}

export const MAX_TRIP_DURATION = 365

export function wouldExceedMaxDuration(
  trip: Pick<Trip, "time_frame" | "draft_duration">,
  eventStartDate: DayObject | null,
  eventEndDate: DayObject | null
): boolean {
  if (!eventStartDate || !eventEndDate) return false

  const tripStart = trip.time_frame.start_date ?? eventStartDate
  const tripEnd = trip.time_frame.start_date
    ? addDays(tripStart, trip.draft_duration - 1)
    : eventEndDate

  const newStart = minDayObject(eventStartDate, tripStart)
  const newEnd = maxDayObject(eventEndDate, tripEnd)

  const newDurationDays = nightsBetweenDayObjects(newStart, newEnd) + 1
  return newDurationDays > MAX_TRIP_DURATION
}
